/**
 *
 * PhoneAuthenticatorSection Component
 *
 */

import {
  Button,
  Dropdown,
  DropdownItem,
  stylize,
  TextField,
} from "@envestnet/envreact-component-library";
import { faExclamationTriangle } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classnames from "classnames";
import CanadaFlag from "images/flags/canada";
import IndiaFlag from "images/flags/india";
import UsFlag from "images/flags/us";
import React, { useEffect, useRef, useState } from "react";
import { PhoneResponsePayload } from "types/PhoneAuthenticator";
import withIntl from "utils/withIntl";
import messages from "./messages";
import styles, { Styles } from "./styles";

interface InjectedProps extends Styles {
  /** Internal - injected from withIntl */
  formatMessage: FormatMessage;

  /** Internal - injected from WithStyles */
  innerRef?: React.RefObject<HTMLDivElement>;
}

interface DefaultProps {
  // Default props should always be required props, i.e. no ? indicating it's optional
  // The default values are specified below
}

export interface PhoneAuthenticatorSectionProps extends DefaultProps {
  /** CSS class name to be applied to top level component */
  className?: string;

  /** Inline style object to be applied to top level component */
  style?: React.CSSProperties;

  sendButtonCallback: (phoneNumber: string) => void;

  cancelCallback: () => void;

  loading: boolean;

  textMessageReceived: PhoneResponsePayload;
}

const defaultProps: DefaultProps = {
  anotherProp: 10,
};

type Props = InjectedProps & PhoneAuthenticatorSectionProps;

const PhoneAuthenticatorSection = (props: Props) => {
  const {
    classes,
    style,
    className,
    formatMessage,
    innerRef,
    sendButtonCallback,
    cancelCallback,
    loading,
    textMessageReceived,
  } = props;
  const phoneNumberRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(
    null
  );
  useEffect(() => {
    phoneNumberRef?.current?.focus();
  }, []);

  const [countryCode, setCountryCode] = useState<string>("us_+1");
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [phoneNumberError, setPhoneNumberError] = useState<string | null>(null);
  const [buttonDisabled, setButtonDisabled] = useState<boolean>(true);

  const phoneNumberPatternValidator = (pNumber: string): boolean => {
    // eslint-disable-next-line
    const phoneNumberPattern1 = /^\d{3}([ \.\-]?)\d{3}\1\d{4}$/;
    const phoneNumberPattern2 = /^\(\d{3}\) ?\d{3}-\d{4}$/;
    return (
      phoneNumberPattern1.test(pNumber) || phoneNumberPattern2.test(pNumber)
    );
  };

  const phoneNumberValidator = (pNumber: string): boolean => {
    let hasError = false;
    if (!pNumber) {
      setPhoneNumberError(formatMessage(messages.requiredPhoneNumber));
      setButtonDisabled(true);
      hasError = true;
    } else {
      const isValid = phoneNumberPatternValidator(pNumber);
      if (!isValid) {
        setPhoneNumberError(formatMessage(messages.invalidPhoneNumber));
        setButtonDisabled(true);
        hasError = true;
      } else {
        setPhoneNumberError(null);
        setButtonDisabled(false);
      }
    }

    return hasError;
  };

  const countryCodeList = [
    {
      flag: <UsFlag />,
      name: formatMessage(messages.us),
      code: "+1",
      abbr: "us",
    },
    {
      flag: <CanadaFlag />,
      name: formatMessage(messages.can),
      code: "+1",
      abbr: "can",
    },
  ];

  if (!window.config?.production) {
    countryCodeList.push({
      flag: <IndiaFlag />,
      name: formatMessage(messages.ind),
      code: "+91",
      abbr: "ind",
    });
  }

  const getCountryCode = (codeAbbr: string): string => {
    switch (codeAbbr) {
      case "us_+1":
      case "can_+1":
        return "+1";

      case "ind_+91":
        return "+91";

      default:
        return "+1";
    }
  };

  return (
    <div
      ref={innerRef}
      className={classnames(className, classes.container)}
      style={style}
    >
      <div className={classnames(classes.header, "header-text-font-family")}>
        {formatMessage(messages.header)}
      </div>
      <div className={classes.dropdownContainer}>
        <div className={classes.countryCodeSection}>
          <div className={classnames(classes.codeLabel, "header-font-family")}>
            Country
          </div>
          <div>
            <Dropdown
              className={classnames(
                classes.codeDropdown,
                "header-text-font-family",
                "no-border-radius"
              )}
              value={countryCode}
              onChange={(newValue) => setCountryCode(newValue)}
              getValueName={(v) => v.split("_")[1]}
              ariaLabel="country code dropdown"
            >
              {countryCodeList.map((countryCodeItem) => (
                <DropdownItem.Basic
                  key={`${countryCodeItem.abbr}_${countryCodeItem.code}`}
                  value={`${countryCodeItem.abbr}_${countryCodeItem.code}`}
                  aria-label={`${countryCodeItem.abbr}_${countryCodeItem.code}`}
                >
                  <span className={classes.flag}>{countryCodeItem.flag}</span>
                  <span className={classes.countryName}>
                    {countryCodeItem.name}
                  </span>
                  <span
                    className={classnames(
                      classes.countryCode,
                      "header-text-font-family"
                    )}
                  >
                    {countryCodeItem.code}
                  </span>
                </DropdownItem.Basic>
              ))}
            </Dropdown>
          </div>
        </div>
        <div className={classes.phoneNumberSection}>
          <div
            className={classnames(
              classes.phoneNumberLabel,
              "header-font-family"
            )}
          >
            Phone Number
          </div>
          <div>
            <TextField
              className={classnames(
                classes.phoneNumberText,
                "header-text-font-family",
                "no-border-radius"
              )}
              inputRef={phoneNumberRef}
              text={phoneNumber}
              textStyle={{ fontFamily: "Red Hat Text" }}
              onChangeText={(val) => setPhoneNumber(val)}
              errorMessage={phoneNumberError}
              onBlur={() => phoneNumberValidator(phoneNumber)}
              aria-label="phone number text field"
              data-testid="phone-number-textfield"
            />
          </div>
        </div>
      </div>
      {textMessageReceived === "ERROR" ? (
        <div className={classes.apiError} data-testid="api-error-container">
          <FontAwesomeIcon
            icon={faExclamationTriangle}
            className={classes.errorIcon}
          />
          <span
            className={classnames(
              classes.errorMessage,
              "header-text-font-family"
            )}
          >
            {formatMessage(messages.apiError)}
          </span>
        </div>
      ) : null}
      <div className={classes.buttonContainer}>
        <Button
          buttonStyle="primary"
          name={formatMessage(messages.submitButton)}
          fullWidth
          className={classnames(
            classes.receiveSmsButton,
            "no-border-radius",
            "header-text-font-family"
          )}
          disabled={buttonDisabled}
          onClick={() =>
            sendButtonCallback(`${getCountryCode(countryCode)}${phoneNumber}`)
          }
          loading={loading}
          aria-label="receive sms button"
        />

        <Button
          buttonStyle="link"
          noUnderline
          name={formatMessage(messages.cancel)}
          className={classnames(
            classes.cancelButton,
            "header-text-font-family"
          )}
          onClick={() => cancelCallback()}
          aria-label="cancel button"
        />
      </div>
    </div>
  );
};

PhoneAuthenticatorSection.defaultProps = defaultProps;

export default withIntl(
  stylize<Props, DefaultProps>(styles)(PhoneAuthenticatorSection)
);
